import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  Package,
  User,
  Scale,
  Award,
  Camera,
  AlertCircle,
  Save,
} from "lucide-react";
import {
  getExchangeItemDetailsThunk,
  verifyExchangeItemThunk,
  rejectExchangeItemThunk,
} from "../../../../store/thunks/adminCartThunks";
import { useExchangeItemForm } from "./exchangeItemFormHooks";
import LoadingSpinner from "../../../../components/common/Loader";

const ExchangeItemVerification = () => {
  const { cartId, itemId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { exchangeItemDetails, loadingExchange, error } = useSelector(
    (state) => state.cart
  );

  const { formData, handleChange, handleSubmit, setFormData } =
    useExchangeItemForm();
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  useEffect(() => {
    dispatch(getExchangeItemDetailsThunk({ cartId, exchangeItemId: itemId }));
  }, [dispatch, cartId, itemId]);

  useEffect(() => {
    if (exchangeItemDetails?.exchangeItem) {
      setFormData({
        grossWeight: exchangeItemDetails.exchangeItem.grossWeight,
        netWeight: exchangeItemDetails.exchangeItem.netWeight,
        stoneWeight: exchangeItemDetails.exchangeItem.stoneWeight || 0,
        purity: exchangeItemDetails.exchangeItem.metalDetails?.purity,
        color: exchangeItemDetails.exchangeItem.metalDetails?.color,
        condition: exchangeItemDetails.exchangeItem.condition,
        notes: "",
      });
    }
  }, [exchangeItemDetails, setFormData]);

  const handleVerify = async () => {
    const result = await dispatch(
      verifyExchangeItemThunk({
        cartId,
        exchangeItemId: itemId,
        data: {
          grossWeight: parseFloat(formData.grossWeight),
          netWeight: parseFloat(formData.netWeight),
          stoneWeight: parseFloat(formData.stoneWeight),
          metalDetails: {
            purity: formData.purity,
            color: formData.color,
          },
          condition: formData.condition,
          notes: formData.notes,
        },
      })
    ).unwrap();

    if (result) {
      navigate("/cart/exchange-items");
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) return;

    const result = await dispatch(
      rejectExchangeItemThunk({
        cartId,
        exchangeItemId: itemId,
        reason: rejectReason,
      })
    ).unwrap();

    if (result) {
      navigate("/cart/exchange-items");
    }
  };

  if (loadingExchange) return <LoadingSpinner />;
  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 text-red-600 p-4 rounded-lg">
          Error loading exchange item: {error}
        </div>
      </div>
    );
  }

  if (!exchangeItemDetails) return null;

  const { cart, exchangeItem } = exchangeItemDetails;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Verify Exchange Item
            </h1>
            <p className="text-gray-600">
              Review and verify customer exchange item
            </p>
          </div>
        </div>
        <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
          Pending Verification
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Customer Info */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <User className="w-5 h-5" />
              Customer Information
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Name</p>
                <p className="font-medium">{cart.user?.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Email</p>
                <p className="font-medium">{cart.user?.email}</p>
              </div>
              {cart.user?.phone && (
                <div>
                  <p className="text-sm text-gray-600">Phone</p>
                  <p className="font-medium">{cart.user.phone}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-gray-600">Cart ID</p>
                <p className="font-medium">{cart._id}</p>
              </div>
            </div>
          </div>

          {/* Item Details Form */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Package className="w-5 h-5" />
              Item Details
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <p className="p-3 bg-gray-50 rounded-lg">
                  {exchangeItem.description}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Item Type
                  </label>
                  <input
                    type="text"
                    value={exchangeItem.itemType}
                    disabled
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Condition *
                  </label>
                  <select
                    value={formData.condition}
                    onChange={(e) => handleChange("condition", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="EXCELLENT">Excellent</option>
                    <option value="GOOD">Good</option>
                    <option value="FAIR">Fair</option>
                    <option value="POOR">Poor</option>
                  </select>
                </div>
              </div>

              {/* Weight Details */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Gross Weight (g) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.grossWeight}
                    onChange={(e) =>
                      handleChange("grossWeight", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Net Weight (g) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.netWeight}
                    onChange={(e) => handleChange("netWeight", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Stone Weight (g)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.stoneWeight}
                    onChange={(e) =>
                      handleChange("stoneWeight", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Metal Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Purity *
                  </label>
                  <select
                    value={formData.purity}
                    onChange={(e) => handleChange("purity", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="24K">24K</option>
                    <option value="22K">22K</option>
                    <option value="18K">18K</option>
                    <option value="14K">14K</option>
                    <option value="925">925 Silver</option>
                    <option value="950">950 Platinum</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Color
                  </label>
                  <select
                    value={formData.color}
                    onChange={(e) => handleChange("color", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="YELLOW">Yellow</option>
                    <option value="WHITE">White</option>
                    <option value="ROSE">Rose</option>
                  </select>
                </div>
              </div>

              {/* Verification Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Verification Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => handleChange("notes", e.target.value)}
                  rows="3"
                  placeholder="Add any notes about the verification..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Images */}
          {exchangeItem.images?.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Camera className="w-5 h-5" />
                Item Images
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {exchangeItem.images.map((image, index) => (
                  <img
                    key={index}
                    src={image}
                    alt={`Item ${index + 1}`}
                    className="w-full h-48 object-cover rounded-lg cursor-pointer hover:opacity-90"
                    onClick={() => window.open(image, "_blank")}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Certifications */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Award className="w-5 h-5" />
              Certifications
            </h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Hallmarked</span>
                <span className="text-sm font-medium">
                  {exchangeItem.isHallmarked ? "Yes" : "No"}
                </span>
              </div>
              {exchangeItem.hallmarkDetails?.huid && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">HUID</span>
                  <span className="text-sm font-medium">
                    {exchangeItem.hallmarkDetails.huid}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Certificate</span>
                <span className="text-sm font-medium">
                  {exchangeItem.hasCertificate ? "Yes" : "No"}
                </span>
              </div>
              {exchangeItem.certificate?.number && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Certificate No.</span>
                  <span className="text-sm font-medium">
                    {exchangeItem.certificate.number}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Estimated Valuation */}
          {exchangeItem.estimatedValue && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Scale className="w-5 h-5" />
                Estimated Valuation
              </h2>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Gross Value</span>
                  <span className="text-sm font-medium">
                    ₹{exchangeItem.estimatedValue.grossValue?.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">
                    Total Deductions
                  </span>
                  <span className="text-sm font-medium text-red-600">
                    -₹
                    {exchangeItem.estimatedValue.totalDeductions?.toLocaleString()}
                  </span>
                </div>
                <div className="border-t pt-3">
                  <div className="flex justify-between">
                    <span className="font-semibold">Final Value</span>
                    <span className="text-lg font-bold text-green-600">
                      ₹
                      {exchangeItem.estimatedValue.finalValue?.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-3">
                *This is an estimate. Final value will be calculated after
                verification.
              </p>
            </div>
          )}

          {/* Customer Notes */}
          {exchangeItem.customerNotes && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-yellow-800">Customer Notes</p>
                  <p className="text-sm text-yellow-700 mt-1">
                    {exchangeItem.customerNotes}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleVerify}
              className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
            >
              <CheckCircle className="w-5 h-5" />
              Verify & Approve
            </button>
            <button
              onClick={() => setShowRejectModal(true)}
              className="w-full px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center justify-center gap-2"
            >
              <XCircle className="w-5 h-5" />
              Reject Item
            </button>
            <button
              onClick={() => navigate(-1)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Reject Exchange Item</h3>
            <p className="text-gray-600 mb-4">
              Please provide a reason for rejecting this exchange item. This
              will be communicated to the customer.
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 mb-4"
              rows="4"
              placeholder="Enter rejection reason..."
            />
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectReason("");
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={!rejectReason.trim()}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Reject Item
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExchangeItemVerification;
